import React, { useState, useEffect } from "react";
import { Button, Switch } from "@mui/material";
import { icons } from "../../../../svg";
import { competitorMenuItems, kochMenuItems } from "../../../../utils/constants";
import { useIsMobile } from "../../../../hooks/useIsMobile";
import { useNitroStore } from "../../../../store/Store";
import TruckIconFO from "../../../../icons/TruckIconFO";
import RailIconFO from "../../../../icons/RailIconFO";
import {
  ActionType,
  OriginDisplayInfo,
  PriceField,
} from "../../../../types/IFavoriteOrigins";

interface OriginRowProps {
  origin: OriginDisplayInfo;
  onRemove: (id: string) => void;
  onUpdate: (id: string, field: PriceField, value: string) => void;
  activeMenuId: string | null;
  setActiveMenuId: (id: string | null) => void;
  onAction: (type: ActionType, origin: OriginDisplayInfo) => void;
  type: string;
  onShowTapHere?: (show: boolean) => void;
}

const PriceInputWithSuffix = ({
  value,
  onChange,
  disabled,
  suffix,
  name,
}: {
  value: string;
  onChange: (val: string) => void;
  disabled: boolean;
  suffix: string;
  name: string;
}) => {
  return (
    <div className={`fo-input-wrapper ${disabled ? "disabled" : ""}`}>
      <input
        id={`price-input-${name}-${suffix}`}
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        className="price-input-field"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        name={name}
        autoComplete="off"
      />
      <span className="price-input-suffix">{suffix}</span>
    </div>
  );
};

const OriginRow: React.FC<OriginRowProps> = ({
  origin,
  onRemove,
  onUpdate,
  activeMenuId,
  setActiveMenuId,
  onAction,
  type,
  onShowTapHere,
}) => {
  const setFavoriteToggleOverride = useNitroStore(
    (s) => s.setFavoriteToggleOverride,
  );
  const initMenuItems = type === "koch" ? kochMenuItems : competitorMenuItems;
  const [showCompetitiveAnalysisModal] = useNitroStore((state) => [
    state.showCompetitiveAnalysisModal,
  ]);
  const isMobile = useIsMobile(768);

  let menuItems = initMenuItems;
  if (isMobile && showCompetitiveAnalysisModal) {
    menuItems = menuItems.filter((item) => item.action !== "overview");
  }
  if (isMobile) {
    menuItems = menuItems.filter((item) => item.action !== "remove");
  }

  const [prices, setPrices] = useState({
    truckMin: origin.truckMin,
    truckMax: origin.truckMax,
    railMin: origin.railMin,
    railMax: origin.railMax,
  });

  const [isTruckSelected, setIsTruckSelected] = useState(
    origin.truckEnabled === "true",
  );
  const [isRailSelected, setIsRailSelected] = useState(
    origin.railEnabled === "true",
  );

  useEffect(() => {
    setPrices({
      truckMin: origin.truckMin,
      truckMax: origin.truckMax,
      railMin: origin.railMin,
      railMax: origin.railMax,
    });
  }, [origin.truckMin, origin.truckMax, origin.railMin, origin.railMax]);

  useEffect(() => {
    setIsTruckSelected(origin.truckEnabled === "true");
    setIsRailSelected(origin.railEnabled === "true");
  }, [origin.truckEnabled, origin.railEnabled]);

  const handleValueChange = (field: PriceField, value: string) => {
    const newPrices = { ...prices, [field]: value };
    setPrices(newPrices);
    onUpdate(origin.id, field, value);

    if ((field === "truckMax" || field === "railMax") && onShowTapHere) {
      const truckMaxValue = field === "truckMax" ? value : newPrices.truckMax;
      const railMaxValue = field === "railMax" ? value : newPrices.railMax;

      const bothEmpty =
        (!truckMaxValue || truckMaxValue.trim() === "") &&
        (!railMaxValue || railMaxValue.trim() === "");

      onShowTapHere(bothEmpty);
    }
  };

  const isMenuOpen = activeMenuId === origin.id;
  const handleMenuToggle = () => setActiveMenuId(isMenuOpen ? null : origin.id);
  const handleActionClick = (action: any) => {
    onAction(action, origin);
    setActiveMenuId(null);
  };

  const isTruckActive = prices.truckMin || prices.truckMax;
  const isRailActive = prices.railMin || prices.railMax;
  const isTruckVisible = isTruckSelected;
  const isRailVisible = isRailSelected;

  const handleTruckToggle = () => {
    const newSelectedState = !isTruckSelected;
    setIsTruckSelected(newSelectedState);
    handleValueChange("truckEnabled", String(newSelectedState));
    setFavoriteToggleOverride(origin.id, { truck: newSelectedState });
  };

  const handleRailToggle = () => {
    const newSelectedState = !isRailSelected;
    setIsRailSelected(newSelectedState);
    handleValueChange("railEnabled", String(newSelectedState));
    setFavoriteToggleOverride(origin.id, { rail: newSelectedState });
  };

  const handleRemove = () => {
    if (isTruckSelected) {
      handleValueChange("truckEnabled", "false");
    }
    if (isRailSelected) {
      handleValueChange("railEnabled", "false");
    }
    onRemove(origin.id);
  };

  const truckGroupClass = `input-group-split ${
    isTruckSelected && (isTruckActive || isTruckSelected) ? "truck-active" : ""
  }`;
  const railGroupClass = `input-group-split ${
    isRailSelected && (isRailActive || isRailSelected) ? "rail-active" : ""
  }`;

  const truckContainerClass = `input-container ${
    isTruckSelected && (isTruckActive || isTruckSelected) ? "truck-active" : ""
  }`;

  const railContainerClass = `input-container ${
    isRailSelected && (isRailActive || isRailSelected) ? "rail-active" : ""
  }`;

  return (
    <div id={`origin-row-${origin.id}`} className="origin-row-card">
      <div className="origin-row-header">
        {type === "koch" ? (
          <img
            data-tooltip-id="location-icon-blue-background"
            src={icons.LocationIconBlueBackground}
            alt="Location Icon Blue Background"
          />
        ) : (
          <img
            data-tooltip-id="location-icon-red-background"
            src={icons.LocationIconRedBackground}
            alt="Location Icon Red Background"
          />
        )}
        <div className="origin-name-mobile">{origin.name}</div>
        <div className="header-actions">
          <div className="action-menu-container">
            {type === "koch" ? (
              <Button
                id="location-overview-button"
                className="action-menu-button"
                onClick={() => onAction("overview", origin)}
              >
                <img
                  src={icons.InformationIconGreyBackground}
                  alt="Location Overview Mobile"
                />
              </Button>
            ) : (
              <Button
                id="action-button"
                className="action-menu-button"
                onClick={handleMenuToggle}
              >
                <img src={icons.ThreeDotIcon} alt="Actions" />
              </Button>
            )}
            {isMenuOpen && (
              <div className="action-menu">
                <div
                  onClick={(e) => e.stopPropagation()}
                  role="menu"
                  tabIndex={-1}
                  onKeyDown={(e) => e.stopPropagation()}
                >
                  {menuItems.map((item) => (
                    <button
                      id={`mobile-${item.id}-id`}
                      key={item.id}
                      type="button"
                      onClick={() =>
                        handleActionClick(item.action as ActionType)
                      }
                      className={`dropdown-menu-item ${item.className}`}
                    >
                      <span>{item.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
          <Button
            id="remove-button"
            className="remove-origin-button"
            onClick={handleRemove}
          >
            <img src={icons.TrashCanIcon} alt="Remove" />
          </Button>
        </div>
      </div>

      <div className="origin-inputs-container split-inputs">
        <div className="mode-row">
          <div className="icon-scale-wrapper">
            <TruckIconFO color={isTruckVisible ? "#60A5FA" : "#9CA3AF"} />
          </div>

          <div className="flex-grow-wrapper">
            <div
              id="truck-input-row"
              className={`${truckGroupClass} ${
                !isTruckSelected ? "disabled-row" : ""
              } clickable-row`}
            >
              <div className="input-row">
                <div
                  className="price-pair price-pair-container"
                  onClick={(e) => {
                    if (isTruckVisible) e.stopPropagation();
                  }}
                  onKeyDown={(e) => {
                    if (isTruckVisible) e.stopPropagation();
                  }}
                  role="presentation"
                >
                  <div
                    className={`${truckContainerClass} input-container-wrapper`}
                  >
                    <PriceInputWithSuffix
                      value={prices.truckMax}
                      onChange={(val) => handleValueChange("truckMax", val)}
                      disabled={!isTruckVisible}
                      suffix="FOB"
                      name={`truckMax-${origin.id}`}
                    />
                  </div>

                  <div
                    className={`${truckContainerClass} input-container-wrapper`}
                  >
                    <PriceInputWithSuffix
                      value={prices.truckMin}
                      onChange={(val) => handleValueChange("truckMin", val)}
                      disabled={!isTruckVisible}
                      suffix="ALT"
                      name={`truckMin-${origin.id}`}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <Switch
            id="truck-toggle-button"
            checked={isTruckVisible}
            onChange={handleTruckToggle}
            className="ios-switch truck-switch"
          />
        </div>

        <div id="rail-mode-row" className="mode-row">
          <div className="icon-scale-wrapper">
            <RailIconFO color={isRailVisible ? "#F97316" : "#9CA3AF"} />
          </div>

          <div className="flex-grow-wrapper">
            <div
              id="rail-input-row"
              className={`${railGroupClass} ${
                !isRailSelected ? "disabled-row" : ""
              } clickable-row`}
            >
              <div className="input-row">
                <div
                  className="price-pair price-pair-container"
                  onClick={(e) => {
                    if (isRailVisible) e.stopPropagation();
                  }}
                  onKeyDown={(e) => {
                    if (isRailVisible) e.stopPropagation();
                  }}
                  role="presentation"
                >
                  <div
                    className={`${railContainerClass} input-container-wrapper`}
                  >
                    <PriceInputWithSuffix
                      value={prices.railMax}
                      onChange={(val) => handleValueChange("railMax", val)}
                      disabled={!isRailVisible}
                      suffix="FOB"
                      name={`railMax-${origin.id}`}
                    />
                  </div>

                  <div
                    className={`${railContainerClass} input-container-wrapper`}
                  >
                    <PriceInputWithSuffix
                      value={prices.railMin}
                      onChange={(val) => handleValueChange("railMin", val)}
                      disabled={!isRailVisible}
                      suffix="ALT"
                      name={`railMin-${origin.id}`}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <Switch
            id="rail-toggle-button"
            checked={isRailVisible}
            onChange={handleRailToggle}
            className="ios-switch rail-switch"
          />
        </div>
      </div>
    </div>
  );
};

export default OriginRow;
